import React, { useMemo, useState } from "react";
import { Ruler, Scissors, Shapes } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { cn } from "../../lib/utils";
import { SHAPES, SHAPE_MAP, buildShape } from "../../lib/sewingShapes";
import FoldViewer from "./FoldViewer";
import PatternPieces, { SeamLegend } from "./PatternPieces";

/* ============================================================
   ShapeLab — the interactive heart of the sub-site: pick a
   primitive, mess with its sliders, scrub the fold. The flat
   pattern and the 3D shape are the same object; matching seam
   colors show which edges find each other when it folds up.
   ============================================================ */

// A choice-type parameter (e.g. which outline) rendered as pill buttons.
const ParamChoice = ({ param, value, onChange }) => (
  <div className="text-sm">
    <span className="font-medium capitalize">{param.label}</span>
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {param.options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(param.key, opt.value)}
          className={cn(
            "rounded-full border px-2.5 py-1 text-xs font-medium transition-all",
            opt.value === value
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-card/60 text-muted-foreground hover:border-primary/50 hover:text-foreground"
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

// One parameter slider row (native range input, shadcn-adjacent styling).
const ParamSlider = ({ param, value, onChange }) => (
  <label className="block text-sm">
    <span className="flex items-baseline justify-between">
      <span className="font-medium capitalize">{param.label}</span>
      <span className="tabular-nums text-muted-foreground">
        {value}
        {param.unit && ` ${param.unit}`}
      </span>
    </span>
    <input
      type="range"
      min={param.min}
      max={param.max}
      step={param.step}
      value={value}
      onChange={(e) => onChange(param.key, Number(e.target.value))}
      className="mt-1 w-full accent-primary"
    />
  </label>
);

const ShapeLab = ({ activeShapeId, setActiveShapeId, paramsByShape, setParamsByShape }) => {
  const shape = SHAPE_MAP[activeShapeId];
  const params = paramsByShape[activeShapeId];
  const [strategyByShape, setStrategyByShape] = useState({});
  const strategies = shape.strategies || null;
  const activeStrategyId =
    strategies && (strategyByShape[activeShapeId] || strategies[0].id);
  const activeStrategy =
    strategies && strategies.find((s) => s.id === activeStrategyId);

  const setParam = (key, value) =>
    setParamsByShape((all) => ({
      ...all,
      [activeShapeId]: { ...all[activeShapeId], [key]: value },
    }));

  const model = useMemo(
    () => buildShape(activeShapeId, params, activeStrategyId || undefined),
    [activeShapeId, params, activeStrategyId]
  );

  return (
    <section>
      <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
        <Shapes className="h-6 w-6 text-primary" />
        Pick a primitive
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Mess with the sliders, scrub the fold. The flat pattern and the 3D
        shape are the same object — matching seam colors show which edges find
        each other when it folds up.
      </p>

      {/* Shape picker */}
      <div className="mt-5 flex flex-wrap gap-2">
        {SHAPES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveShapeId(s.id)}
            className={cn(
              "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all",
              s.id === activeShapeId
                ? "border-primary bg-primary text-primary-foreground shadow"
                : "border-border bg-card/60 text-muted-foreground hover:border-primary/50 hover:text-foreground"
            )}
          >
            {s.name}
            <span className="ml-1.5 hidden text-xs font-normal opacity-70 sm:inline">
              {s.tagline}
            </span>
          </button>
        ))}
      </div>

      {/* Construction picker — same shape, different real-world builds. */}
      {strategies && (
        <div className="mt-4 rounded-xl border border-primary/25 bg-primary/5 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            construction — same box, four ways to actually sew it
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {strategies.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() =>
                  setStrategyByShape((all) => ({ ...all, [activeShapeId]: s.id }))
                }
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-all",
                  s.id === activeStrategyId
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card/60 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                )}
              >
                {s.name}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs italic text-muted-foreground">
            {activeStrategy.blurb}
          </p>
          <dl className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-3">
            {Object.entries(activeStrategy.tradeoffs).map(([k, v]) => (
              <div key={k}>
                <dt className="font-semibold capitalize text-foreground/80">{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="mt-5 grid gap-5 lg:grid-cols-[1.2fr,1fr]">
        {/* 3D fold view */}
        <Card className="bg-card/60 backdrop-blur">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{shape.name} — flat ↔ sewn</CardTitle>
            <CardDescription>{model.insight}</CardDescription>
          </CardHeader>
          <CardContent>
            <FoldViewer model={model} />
            <div className="mt-4 rounded-lg border bg-background/50 p-3">
              <p className="mb-2 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground">
                seam key — same color = sew these edges together
              </p>
              <SeamLegend model={model} />
            </div>
          </CardContent>
        </Card>

        {/* Sliders + drafting table */}
        <div className="flex flex-col gap-5">
          <Card className="bg-card/60 backdrop-blur">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Ruler className="h-4 w-4 text-primary" />
                Stretch it
              </CardTitle>
              <CardDescription>
                Changing a primitive never changes its pattern's structure —
                only the numbers. Watch the pieces below recompute.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {shape.params.map((p) =>
                p.type === "choice" ? (
                  <ParamChoice key={p.key} param={p} value={params[p.key]} onChange={setParam} />
                ) : (
                  <ParamSlider key={p.key} param={p} value={params[p.key]} onChange={setParam} />
                )
              )}
            </CardContent>
          </Card>

          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <Scissors className="h-4 w-4" />
              What you'd cut
            </h3>
            <PatternPieces model={model} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShapeLab;
